import axios from "axios";

export const MAX_STORY_LENGTH = 4000;
export const MIN_STORY_LENGTH = 10;

const normalizeStory = story => String(story || "").normalize("NFC").trim();

export function memoryApiBase() {
  const value = String(process.env.REACT_APP_MEMORY_API_URL || "").trim();
  let url;
  try { url = new URL(value); } catch { throw new Error("MEMORY_API_NOT_CONFIGURED"); }
  if (url.username || url.password || /[?#]/.test(value) ||
      (url.protocol !== "https:" && !(url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname)))) {
    throw new Error("MEMORY_API_NOT_CONFIGURED");
  }
  return url.href.replace(/\/+$/, "");
}

export function validateStory(story, consent) {
  const text = normalizeStory(story);
  const length = Array.from(text).length;
  if (length < MIN_STORY_LENGTH) return "사진에 담긴 이야기를 10자 이상 들려주세요.";
  if (length > MAX_STORY_LENGTH) return "이야기는 4,000자 이내로 입력해 주세요.";
  const hasControlCharacters = Array.from(text).some(character => {
    const code = character.charCodeAt(0);
    return (code < 32 && ![9, 10, 13].includes(code)) || code === 127;
  });
  if (hasControlCharacters) return "이야기에 보이지 않는 제어 문자가 있습니다. 해당 부분을 지우고 다시 입력해 주세요.";
  if (consent !== true) return "사진과 이야기를 전시 체험에 활용하는 데 동의해 주세요.";
  return "";
}

export async function registerMemory({ ticketId, story, consent, language = 'ko' }, signal) {
  const validation = validateStory(story, consent);
  if (validation) throw new Error(validation);
  if (!['ko', 'en'].includes(language)) throw new Error('INVALID_LANGUAGE');
  const { data } = await axios.post(`${memoryApiBase()}/v1/memories`, {
    ticketId, story: normalizeStory(story), consent: true, language,
  }, { timeout: 20000, signal });
  if (!data?.requestId || !["queued", "processing", "completed", "failed"].includes(data.status)) {
    throw new Error("INVALID_MEMORY_RESPONSE");
  }
  return data;
}

export async function getMemoryStatus(ticketId, signal) {
  const { data } = await axios.get(`${memoryApiBase()}/v1/memories/${encodeURIComponent(ticketId)}`, {
    timeout: 15000, signal,
  });
  if (!["queued", "processing", "completed", "failed"].includes(data?.status)) throw new Error("INVALID_MEMORY_RESPONSE");
  return data;
}

export function memoryErrorCode(error) {
  return error?.response?.data?.error?.code || error?.response?.data?.code || error?.message;
}

export function isMissingMemory(error) {
  return error?.response?.status === 404 && memoryErrorCode(error) === "MEMORY_NOT_FOUND";
}

export function memoryLookupErrorMessage(error, pendingSubmission = false) {
  if (["MEMORY_API_NOT_CONFIGURED", "MEMORY_EXPIRED"].includes(memoryErrorCode(error))) return memoryErrorMessage(error);
  if (pendingSubmission) return "이야기 접수 결과를 아직 확인하지 못했습니다. 다른 내용이 접수되지 않도록 입력을 잠갔습니다. 접수 상태를 확인하거나 같은 내용으로 다시 시도해 주세요.";
  return "이야기 등록 상태를 확인하지 못했습니다. 접수 상태를 다시 확인하거나 현장 스태프에게 문의해 주세요.";
}

export function memoryErrorMessage(error) {
  const code = memoryErrorCode(error);
  if (code === "MEMORY_API_NOT_CONFIGURED") return "이야기 접수 서비스가 아직 연결되지 않았습니다. 현장 스태프에게 문의해 주세요.";
  if (code === "MEMORY_EXPIRED") return "이야기의 보관 기간이 끝났습니다. 현장 스태프에게 문의해 주세요.";
  if (code === "INPUT_FROZEN") return "이미 접수된 사진과 이야기는 변경할 수 없습니다. 등록 상태를 다시 확인해 주세요.";
  if (error?.response?.status === 429) return "접수가 잠시 몰리고 있어요. 잠시 후 같은 내용으로 다시 시도해 주세요.";
  return "이야기를 접수하지 못했습니다. 사진은 다시 올리지 않아도 됩니다. 같은 내용으로 다시 시도해 주세요.";
}
